import React, { FC } from 'react';
import styled from 'styled-components';
import { TextField, Slider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Variables from '../../../design-system/variables';
import Colours from '../../../design-system/colours';

interface OvErrorMarginControlProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
}

const OvErrorMarginControl: FC<OvErrorMarginControlProps> = ({
  value,
  onChange,
  min = 1,
  max = 20,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    onChange(newValue as number);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    if (!isNaN(newValue) && newValue >= min && newValue <= max) {
      onChange(newValue);
    }
  };

  return (
    <Container>
      <Label>{t('lots.calibration.errorMargin')}</Label>
      <ControlWrapper>
        <Slider
          value={value}
          onChange={handleSliderChange}
          min={min}
          max={max}
          step={1}
          disabled={disabled}
          valueLabelDisplay="auto"
          sx={{
            width: '200px',
            marginRight: '1rem',
          }}
        />
        <StyledTextField
          type="number"
          value={value}
          onChange={handleInputChange}
          inputProps={{
            min,
            max,
            step: 1,
          }}
          disabled={disabled}
          size="small"
        />
      </ControlWrapper>
    </Container>
  );
};

export default OvErrorMarginControl;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background-color: ${Colours.OV_WHITE};
  border-radius: ${Variables.borderRadius.SMALL};
  border: 1px solid ${Colours.GRAY['200']};
`;

const Label = styled.label`
  font-size: ${Variables.fontSizes.MEDIUM};
  font-weight: bold;
  color: ${Colours.OV_BASE};
`;

const ControlWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StyledTextField = styled(TextField)`
  && {
    width: 80px;
  }
`;

